import { useEffect, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { Save, Plus, Trash2, BarChart3, CreditCard, Users, Building2, Share2, Info } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none";
const Card = ({ icon: Icon, title, hint, children }) => (
  <div className="border border-line bg-surface p-6">
    <div className="mb-4 flex items-start gap-3">
      <Icon className="mt-0.5 h-5 w-5 text-saffron" />
      <div><h2 className="font-serif-latin text-xl text-ink">{title}</h2>{hint && <p className="mt-0.5 text-sm text-ink-3">{hint}</p>}</div>
    </div>
    {children}
  </div>
);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAdminSettings().then((d) => setS({
    ga4_id: d.ga4_id || "", gsc_code: d.gsc_code || "", razorpay_key_id: d.razorpay_key_id || "",
    razorpay_secret_set: d.razorpay_secret_set, donation_presets: d.donation_presets || [101, 501, 1100, 2100],
    org: d.org || {}, team: d.team || [], socials: d.socials || {},
    content_disclaimer: d.content_disclaimer || { enabled: true, title: "", body: "", scope: "all", show_until: "" },
  })); }, []);

  if (!s) return <p className="py-16 text-center text-ink-3">…</p>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ga4_id: s.ga4_id, gsc_code: s.gsc_code, razorpay_key_id: s.razorpay_key_id,
        donation_presets: s.donation_presets, org: s.org, team: s.team, socials: s.socials,
        content_disclaimer: s.content_disclaimer,
      };
      if (secret) payload.razorpay_key_secret = secret;
      const res = await adminUpdateSettings(payload);
      setS({ ...s, razorpay_secret_set: res.razorpay_secret_set });
      setSecret("");
      toast("Settings saved", { description: "Applied across the platform." });
    } catch (err) { toast.error("Save failed", { description: err.message }); }
    finally { setBusy(false); }
  };

  const setOrg = (k, v) => setS({ ...s, org: { ...s.org, [k]: v } });
  const setDisc = (k, v) => setS({ ...s, content_disclaimer: { ...s.content_disclaimer, [k]: v } });
  const setTeam = (i, k, v) => setS({ ...s, team: s.team.map((m, idx) => idx === i ? { ...m, [k]: v } : m) });
  const addMember = () => setS({ ...s, team: [...s.team, { name: "", role: "", bio: "", image: "" }] });
  const removeMember = (i) => setS({ ...s, team: s.team.filter((_, idx) => idx !== i) });

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-serif-latin text-3xl text-ink">Site Settings</h1>
        <button data-testid="settings-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}</button>
      </div>

      <div className="space-y-6">
        <Card icon={Info} title="Content Disclaimer Note" hint="A small note shown at the top of the site. It is always visible (not dismissable) so every visitor sees it on each page load. Choose whether it appears on every page or only on content & knowledge pages.">
          <label className="mb-3 flex items-center gap-2 text-sm text-ink-2">
            <input data-testid="disclaimer-enabled" type="checkbox" checked={s.content_disclaimer.enabled !== false} onChange={(e) => setDisc("enabled", e.target.checked)} className="h-4 w-4 accent-[var(--saffron)]" />
            Show this note
          </label>
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Where to show</label>
              <select data-testid="disclaimer-scope" value={s.content_disclaimer.scope || "all"} onChange={(e) => setDisc("scope", e.target.value)} className={inp}>
                <option value="all">Every page (whole site)</option>
                <option value="content">Content &amp; knowledge pages only</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Show until (optional)</label>
              <div className="flex items-center gap-2">
                <input data-testid="disclaimer-show-until" type="date" value={s.content_disclaimer.show_until || ""} onChange={(e) => setDisc("show_until", e.target.value)} className={inp} />
                {s.content_disclaimer.show_until && (
                  <button type="button" data-testid="disclaimer-clear-until" onClick={() => setDisc("show_until", "")} className="shrink-0 border border-line px-3 py-2.5 text-xs text-ink-3 hover:text-[var(--saffron-dark)]">Clear</button>
                )}
              </div>
              <p className="mt-1 text-xs text-ink-3">After this date the note hides itself automatically. Leave blank to show indefinitely.</p>
            </div>
          </div>
          <p className="mb-3 text-xs text-ink-3">“Content &amp; knowledge pages only” keeps it off the homepage and other pages if it ever feels heavy.</p>
          <div className="mb-3">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Title (optional)</label>
            <input data-testid="disclaimer-title" value={s.content_disclaimer.title || ""} onChange={(e) => setDisc("title", e.target.value)} placeholder="e.g. A note on our ongoing development" className={inp} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Note text</label>
            <textarea data-testid="disclaimer-body" value={s.content_disclaimer.body || ""} onChange={(e) => setDisc("body", e.target.value)} rows={12} placeholder="Enter the disclaimer copy. Leave a blank line between paragraphs." className={`${inp} resize-y leading-relaxed`} />
            <p className="mt-1 text-xs text-ink-3">Line breaks and blank lines between paragraphs are preserved.</p>
          </div>
        </Card>

        <Card icon={BarChart3} title="Analytics & Search Console" hint="Paste your Google IDs; the site injects the tracking script and verification tag automatically.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">GA4 Measurement ID</label><input data-testid="ga4-input" value={s.ga4_id} onChange={(e) => setS({ ...s, ga4_id: e.target.value })} placeholder="G-XXXXXXXXXX" className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Search Console verification</label><input data-testid="gsc-input" value={s.gsc_code} onChange={(e) => setS({ ...s, gsc_code: e.target.value })} placeholder="verification content value" className={inp} /></div>
          </div>
        </Card>

        <Card icon={CreditCard} title="Donations (Razorpay)" hint="Your keys are stored securely and used to accept donations on the Support Us page.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Razorpay Key ID</label><input data-testid="rzp-key" value={s.razorpay_key_id} onChange={(e) => setS({ ...s, razorpay_key_id: e.target.value })} placeholder="rzp_live_xxx / rzp_test_xxx" className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Razorpay Key Secret {s.razorpay_secret_set && <span className="text-saffron">· set</span>}</label><input data-testid="rzp-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={s.razorpay_secret_set ? "•••••••• (leave blank to keep)" : "Enter secret"} className={inp} /></div>
          </div>
          <div className="mt-4">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Preset amounts (₹, comma-separated)</label>
            <input data-testid="presets-input" value={s.donation_presets.join(", ")} onChange={(e) => setS({ ...s, donation_presets: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter(Boolean) })} className={inp} />
          </div>
        </Card>

        <Card icon={Building2} title="Organisation" hint="Shown in donations, receipts and the footer.">
          <div className="grid gap-4 sm:grid-cols-2">
            <input value={s.org.name || ""} onChange={(e) => setOrg("name", e.target.value)} placeholder="Organisation name" className={inp} />
            <input value={s.org.email || ""} onChange={(e) => setOrg("email", e.target.value)} placeholder="Contact email" className={inp} />
          </div>
        </Card>

        <Card icon={Share2} title="Social media links" hint="Paste full URLs; icons appear in the footer. Leave blank to hide.">
          <div className="grid gap-4 sm:grid-cols-2">
            {["twitter", "instagram", "youtube", "facebook", "telegram", "whatsapp"].map((k) => (
              <div key={k}>
                <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{k}</label>
                <input data-testid={`social-input-${k}`} value={s.socials[k] || ""} onChange={(e) => setS({ ...s, socials: { ...s.socials, [k]: e.target.value } })} placeholder={`https://…`} className={inp} />
              </div>
            ))}
          </div>
        </Card>

        <Card icon={Users} title="Team (About page)" hint="Add the people behind Dharmartha. Shown on the About page.">
          <div className="space-y-4">
            {s.team.map((m, i) => (
              <div key={i} data-testid={`team-row-${i}`} className="grid gap-2 border border-line p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <input value={m.name} onChange={(e) => setTeam(i, "name", e.target.value)} placeholder="Name" className={inp} />
                <input value={m.role} onChange={(e) => setTeam(i, "role", e.target.value)} placeholder="Role" className={inp} />
                <button onClick={() => removeMember(i)} className="flex items-center justify-center px-3 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                <input value={m.image || ""} onChange={(e) => setTeam(i, "image", e.target.value)} placeholder="Photo URL (optional)" className={`${inp} sm:col-span-1`} />
                <input value={m.bio || ""} onChange={(e) => setTeam(i, "bio", e.target.value)} placeholder="Short bio (optional)" className={`${inp} sm:col-span-2`} />
              </div>
            ))}
            <button data-testid="add-team" onClick={addMember} className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink"><Plus className="h-4 w-4" /> Add member</button>
          </div>
        </Card>
      </div>
    </div>
  );
}
